//changed due to package upgrade
import { useState } from 'react';
import Modal from '@mui/material/Modal';
import {
  Box,
  alpha,
  MenuItem,
  Stack,
  Button,
  Grid,
  FormHelperText,
  Autocomplete,
  IconButton,
} from '@mui/material';
import IntlMessages from '@projectK/helpers/IntlMessages';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import AppTextField from '@projectK/components/AppTextField';
import AppGridContainer from '@projectK/components/AppGridContainer';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import jwtAxios from '@projectK/services/auth/JWT';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTime } from 'luxon';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { useApplicationSettings } from '@projectK/context/VerbiageContextProvider';
import AppModalConfirmation from '@projectK/components/AppModalConfirmation';
import { CloseRounded } from '@mui/icons-material';
import PropTypes from 'prop-types';
const validationSchema = yup.object({
  promo_code_name: yup
    .string()
    .matches(
      /^[a-zA-Z0-9@!_-]+(?: [a-zA-Z0-9@!_-]+)*$/,
      () => <IntlMessages id="validation.promoCodeName.pattern" />
    )
    .max(100, () => <IntlMessages id="validation.promoCodeName.maxLength" />)
    .required(<IntlMessages id='validation.promonameRequired' />),
  code: yup
    .string()
    .matches(
      /^[a-zA-Z0-9@!_-]+(?: [a-zA-Z0-9@!_-]+)*$/,
      () => <IntlMessages id="validation.code.pattern" />
    )
    .required(<IntlMessages id='validation.promocodeRequired' />)
   .max(20, <IntlMessages id='validation.promoCodeTooLong' />)
   .matches(/^\S*$/, () => <IntlMessages id='validation.promoCodeNoSpaces' />),
  start_date: yup
    .date()
    .typeError(<IntlMessages id='validation.dateInvalid' />)
    .nullable()
    .test({
      name: 'notFutureDate',
      exclusive: false,
      message: <IntlMessages id='validation.startDateNotFuture' />,
      test: function (value) {
        const today = new Date();
        const selectedDate = new Date(value);

        // Check if the selected date is today or in the past
        return (
          !value ||
          selectedDate.setHours(0, 0, 0, 0) >= today.setHours(0, 0, 0, 0)
        );
      },
    }),
  end_date: yup
    .date()
    .typeError(<IntlMessages id='validation.dateInvalid' />)
    .nullable()
    .test({
      name: 'greaterThanStart',
      exclusive: false,
      message: <IntlMessages id='validation.endDateBeforeStart' />,
      test: function (value, { parent }) {
        const startDate = parent.start_date;
        return !value || !startDate || new Date(value) >= new Date(startDate);
      },
    }),
  usage_limit: yup.number().typeError(<IntlMessages id='validation.usageLimitInvalid' />).nullable(),
  discount_value: yup
    .number()
    .typeError(<IntlMessages id='validation.discountValueInvalid' />)
    .required(<IntlMessages id='validation.discountValueRequired' />)
    .min(0, <IntlMessages id='validation.discountValueTooLow' />)
    .max(9999999.99, <IntlMessages id='validation.discountValueTooHigh' />)
    .test({
      name: 'maxPercentage',
      test: function (value) {//NOSONAR
        const discountType = this.resolve(yup.ref('discount_type'));
        const isPercentage = discountType === 'percentage'; // NOSONAR: Remove this "===" check; it will always be false.

        if (isPercentage && value > 100) {
          return this.createError({
            path: this.path,
            message: <IntlMessages id='validation.discountPercentageTooHigh' />,
          });
        }

        return true;
      },
    }),
  discount_type: yup
    .string()
    .required(<IntlMessages id='validation.promotionTypeRequired' />)
    .oneOf(['percentage', 'fixed'], <IntlMessages id='validation.promotionTypeInvalid' />),
    product_id: yup.string().when('service_type', {
      is: 'product',
      then: (schema) =>
        schema.required(<IntlMessages id='validation.productRequired' />),
      otherwise: (schema) => schema.nullable(),
    }),

    tournament_id: yup.string().when('service_type', {
      is: 'tournament',
      then: (schema) =>
        schema.required(<IntlMessages id='validation.tournamentRequired' />),
      otherwise: (schema) => schema.nullable(),
    }),

});

// here  tournament_id coming when we use AddPromoCode in tournament settings rest of the place when we use Add PromoCode don't send component and tournament_id
const AddPromoCode = ({
  isOpen,
  onClose,
  fetchData,
  productOptions,
  tournamentOptions,
  tournamentId,
  hasTournamentAddon,
}) => {
  const companyTheme = CompanyTheme();
  const { verbiageSettings } = useApplicationSettings();
  const infoViewActionsContext = useInfoViewActionsContext();
  const [selectedProductOption, setSelectedProductOption] = useState(null);
  const [selectedTournamentOption, setSelectedTournamentOption] = useState(
    tournamentId || null,
  );
  const [serviceType, setServiceType] = useState(
    tournamentId ? 'tournament' : '',
  );
  const [openDialog, setOpenDialog] = useState(false);
  const handleClose = () => {
    setSelectedProductOption(null);
  };
  const handleDialogOpen = () => {
    setOpenDialog(true);
    setServiceType('');
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleCloseModalDialog = () => {
    setOpenDialog(false); // to close the dialog
    handleModalClose(); //to close the modal
  };
  const handleModalClose = () => {
    handleClose();
    onClose();
    setServiceType('');
  };
  return (
    <>
      <Modal open={isOpen} onClose={handleDialogOpen}>
        <Box
          className='p-popup'
          sx={{
            bgcolor: 'background.paper',
          }}
        >
          <Formik
            initialValues={{
              promo_code_name: '',
              code: '',
              start_date: null,
              end_date: null,
              usage_limit: null,
              discount_type: '',
              discount_value: '',
              product_id: null,
              tournament_id: tournamentId ?? null,
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true);
              const payload = { ...values };
              if (serviceType === 'tournament') {
                payload.tournament_id = selectedTournamentOption;
              }
              delete payload.service_type;
              delete payload.product_id;

              try {
                const response = await jwtAxios.post(`/promo-codes`, payload);
                if (serviceType === 'product' && selectedProductOption) {
                  await jwtAxios.post(
                    `/product-promocode/${selectedProductOption}/${response.data.id}`,
                    { product_id: selectedProductOption }
                  );
                }

                setSubmitting(false);
                handleCloseModalDialog();
                fetchData();
                infoViewActionsContext.showMessage(
                  'New Promo Code has been created successfully!'
                );
              } catch (error) {
                infoViewActionsContext.fetchError(error.response.data.error);
                setSubmitting(false);
              }
            }}

          >
            {({
              setFieldValue,
              values,
              handleSubmit,
              handleChange,
              isSubmitting,
              setErrors,
              touched,
              errors,
            }) => (
              <Form noValidate autoComplete='off'>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography
                      variant='h4'
                      sx={{
                        color: companyTheme
                          ? companyTheme.primary.main
                          : 'text.primary',
                      }}
                    >
                      Add Promo Code
                    </Typography>
                    <Typography
                      variant='body1'
                      sx={{ mt: 2, color: 'text.secondary' }}
                    >
                      All promo codes at company level apply to all shopping
                      cart purchases.
                    </Typography>
                  </Box>
                  <IconButton
                    size='small'
                    sx={{ fontWeight: 'bold', color: '#000000' }}
                    className='icon-btn'
                    onClick={handleDialogOpen}
                  >
                    <CloseRounded />
                  </IconButton>
                </Box>

                <AppGridContainer sx={{ mt: 2 }}>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <Box
                      sx={{
                        border: '1px solid #EAECF0',
                        p: 6,
                        borderRadius: 3,
                        boxShadow:
                          '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                      }}
                    >
                      <AppGridContainer>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <AppTextField
                            name='promo_code_name'
                            variant='outlined'
                            sx={{
                              width: '100%',

                              my: 2,
                            }}
                            label='Name'
                            inputProps={{ maxLength: 101 }}
                          />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                          <AppTextField
                            name='code'
                            variant='outlined'
                            sx={{
                              width: '100%',

                              my: 2,
                            }}
                            label='Promo Code'
                            inputProps={{ maxLength: 21 }}
                          />
                        </Grid>
                        {!tournamentId && (
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              sx={{ width: '100%' }}
                              select
                              name='service_type'
                              labelId='discount-type-select-label'
                              label='Service Type'
                              id='service-type-select'
                              value={serviceType}
                              onChange={(e) => {
                                setServiceType(e.target.value);
                                setFieldValue("service_type", e.target.value);
                              }}
                            >
                              <MenuItem value=''>None</MenuItem>
                              <MenuItem value='product'>Products</MenuItem>
                              {hasTournamentAddon && (<MenuItem value='tournament'>
                                Tournaments
                              </MenuItem>)}
                            </TextField>
                          </Grid>
                        )}
                        {serviceType === 'product' && (
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                              sx={{
                                width: '100%',
                              }}
                              options={(productOptions || []).sort((a, b) =>
                                a.product_name.localeCompare(
                                  b.product_name,
                                  undefined,
                                  {
                                    numeric: true,
                                    sensitivity: 'base',
                                  },
                                ),
                              )}
                              getOptionLabel={(option) =>
                                option.badge
                                  ? `${option.product_name} - ${option.badge}`
                                  : option.product_name
                              }
                              onChange={(event, newValue) => {
                                const selectedProductId = newValue ? newValue.id : '';
                                setSelectedProductOption(selectedProductId);
                                setFieldValue("product_id", selectedProductId);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={
                                    verbiageSettings.product_type || 'Products'
                                  }
                                  error={
                                    touched.product_id &&
                                    Boolean(errors.product_id)
                                  }
                                  helperText={
                                    touched.product_id && errors.product_id
                                      ? 'Please choose a product.'
                                      : ''
                                  }
                                />
                              )}
                            />
                          </Grid>
                        )}
                        {serviceType === 'tournament' && !tournamentId && (
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                              sx={{
                                width: '100%',
                              }}
                              value={tournamentOptions.find(
                                (tournament) =>
                                  tournament.id === selectedTournamentOption,
                              )}
                              options={(tournamentOptions || []).sort((a, b) =>
                                a.tournament_name.localeCompare(
                                  b.tournament_name,
                                  undefined,
                                  {
                                    numeric: true,
                                    sensitivity: 'base',
                                  },
                                ),
                              )}
                              getOptionLabel={(option) => {
                                return option.tournament_name;
                              }}
                              onChange={(event, newValue) => {
                                const selectedTournamentId = newValue ? newValue.id : '';
                                setSelectedTournamentOption(selectedTournamentId);
                                setFieldValue("tournament_id", selectedTournamentId);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={'Tournaments'}
                                  error={
                                    touched.tournament_id &&
                                    Boolean(errors.tournament_id)
                                  }
                                  helperText={
                                    touched.tournament_id &&
                                    errors.tournament_id
                                      ? 'Please choose a Tournament'
                                      : ''
                                  }
                                />
                              )}
                            />
                          </Grid>
                        )}
                        <Grid size={{ xs: 12, md: 6 }}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Stack spacing={1}>
                              <DatePicker
                                disablePast
                                label='Start Date'
                                error={
                                  touched.start_date &&
                                  Boolean(errors.start_date)
                                }
                                helperText={
                                  touched.start_date && errors.start_date
                                }
                                onChange={(date) => {
                                  if (date) {
                                    setFieldValue(
                                      'start_date',
                                      DateTime.fromJSDate(date).toFormat(
                                        'yyyy-MM-dd',
                                      ),
                                    );
                                  } else {
                                    setFieldValue('start_date', true);
                                  }
                                  setErrors({ start_date: undefined });

                                  touched.start_date = true;
                                }}
                                name='start_date'
                                onError={(newError) =>
                                  setErrors({ start_date: newError })
                                }
                              />
                              <FormHelperText
                                style={{ color: '#d32f2f', marginLeft: '15px' }}
                              >
                                {touched.start_date && errors.start_date}
                              </FormHelperText>
                            </Stack>
                          </LocalizationProvider>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Stack spacing={1}>
                              <DatePicker
                                disablePast
                                label='End Date'
                                onChange={(date) => {
                                  if (date) {
                                    setFieldValue(
                                      'end_date',
                                      DateTime.fromJSDate(date).toFormat(
                                        'yyyy-MM-dd',
                                      ),
                                    );
                                  } else {
                                    setFieldValue('end_date', true);
                                  }
                                  setErrors({ end_date: undefined });

                                  touched.end_date = true;
                                }}
                                name='end_date'
                                onError={(newError) =>
                                  setErrors({ end_date: newError })
                                }
                              />
                              <FormHelperText
                                style={{ color: '#d32f2f', marginLeft: '15px' }}
                              >
                                {touched.end_date && errors.end_date}
                              </FormHelperText>
                            </Stack>
                          </LocalizationProvider>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            name='usage_limit'
                            variant='outlined'
                            value={values?.usage_limit}
                            error={
                              touched.usage_limit && Boolean(errors.usage_limit)
                            }
                            helperText={
                              (touched.usage_limit && errors.usage_limit) ||
                              'Specify the maximum number of times promocode can be used.'
                            }
                            onChange={(event) => {
                              setFieldValue(
                                'usage_limit',
                                event.target.value === ''
                                  ? null
                                  : event.target.value,
                              );
                            }}
                            sx={{
                              width: '100%',
                            }}
                            label='Limit'
                          />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            sx={{ width: '100%' }}
                            select
                            name='discount_type'
                            labelId='discount-type-select-label'
                            label='Promotion Type'
                            id='discount-type-select'
                            value={values.discount_type}
                            error={
                              touched.discount_type &&
                              Boolean(errors.discount_type)
                            }
                            helperText={
                              touched.discount_type && errors.discount_type
                            }
                            onChange={handleChange}
                          >
                            <MenuItem value='fixed'>Fixed</MenuItem>
                            <MenuItem value='percentage'>Percentage</MenuItem>
                          </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                          <AppTextField
                            name='discount_value'
                            variant='outlined'
                            sx={{
                              width: '100%',
                            }}
                            label='Value'
                          />
                        </Grid>

                        <Grid size={{ xs: 12, md: 12 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Button
                              sx={{
                                position: 'relative',
                                minWidth: 100,
                                backgroundColor: companyTheme?.primary?.main,
                                '&:hover': {
                                  backgroundColor:
                                    companyTheme &&
                                    alpha(companyTheme.primary.main, 0.8),
                                },
                              }}
                              variant='contained'
                              type='submit'
                            >
                              <IntlMessages id='common.save' />
                            </Button>

                            <Button
                              sx={{
                                position: 'relative',
                                minWidth: 100,
                                ml: 2.5,
                                borderColor: companyTheme?.primary?.main,
                                '&:hover': {
                                  borderColor:
                                    companyTheme &&
                                    alpha(companyTheme.primary.main, 0.8),
                                },
                                color: companyTheme
                                  ? companyTheme.primary.main
                                  : 'primary',
                              }}
                              variant='outlined'
                              onClick={handleDialogOpen}
                            >
                              <IntlMessages id='common.cancel' />
                            </Button>
                          </Box>
                        </Grid>
                      </AppGridContainer>
                    </Box>
                  </Grid>
                </AppGridContainer>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
      <AppModalConfirmation
        openDialog={openDialog}
        handleCloseDialog={handleCloseDialog}
        handleCloseModalDialog={handleCloseModalDialog}
      />
    </>
  );
};

export default AddPromoCode;
AddPromoCode.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  fetchData: PropTypes.func,
  productOptions: PropTypes.array,
  tournamentOptions: PropTypes.array,
  tournamentId: PropTypes.number,
  hasTournamentAddon: PropTypes.bool,
};
